import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../contexts/ToastContext';
import { t } from '../../i18n/nl.js';

const TEKST_KEYS = [
  'invoice_footer',
  'invoice_footer_draft',
  'invoice_footer_sent',
  'invoice_footer_reminded',
  'invoice_footer_final_notice',
  'invoice_footer_collections',
  'reminder_pdf_text',
  'final_notice_pdf_text',
];

export default function FactuurtekstenPage() {
  const { showSuccess, showError } = useToast();
  const [fields, setFields] = useState({
    invoice_footer: '',
    invoice_footer_draft: '',
    invoice_footer_sent: '',
    invoice_footer_reminded: '',
    invoice_footer_final_notice: '',
    invoice_footer_collections: '',
    reminder_pdf_text: '',
    final_notice_pdf_text: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    async function load() {
      setLoading(true);
      setError('');
      try {
        const { data, error: err } = await supabase
          .from('settings')
          .select('key, value')
          .in('key', TEKST_KEYS);
        if (err) throw err;
        const map = {};
        (data || []).forEach(({ key, value }) => { map[key] = value ?? ''; });
        setFields(prev => ({ ...prev, ...Object.fromEntries(TEKST_KEYS.map(k => [k, map[k] ?? prev[k]])) }));
      } catch (err) {
        setError(t('factuurteksten.foutLaden') + err.message);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const rows = Object.entries(fields).map(([key, value]) => ({ key, value }));
      const { error: err } = await supabase
        .from('settings')
        .upsert(rows, { onConflict: 'key' });
      if (err) throw err;
      showSuccess(t('factuurteksten.opgeslagen'));
    } catch (err) {
      setError(t('factuurteksten.foutOpslaan') + err.message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <div style={{ marginBottom: 6 }}>
            <Link to="/instellingen" className="text-muted" style={{ fontSize: 13 }}>
              ← {t('instellingen.title')}
            </Link>
          </div>
          <h1 className="page-title">{t('factuurteksten.title')}</h1>
          <p className="page-subtitle">{t('factuurteksten.subtitle')}</p>
        </div>
      </div>

      {error && <div className="error-msg" style={{ marginBottom: 20 }}>{error}</div>}

      <div className="card">
        <form onSubmit={handleSave}>
          {/* Sectie 1: Algemene factuurvoetnoot */}
          <h3 style={{ fontSize: 13, fontWeight: 600, marginBottom: 12, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {t('factuurteksten.algemeenTitel')}
          </h3>
          <div className="form-group" style={{ marginBottom: 32 }}>
            <label className="form-label">{t('factuurteksten.labelAlgemeneVoetnoot')}</label>
            <textarea
              className="form-input"
              rows={3}
              value={fields.invoice_footer}
              onChange={e => setFields(p => ({ ...p, invoice_footer: e.target.value }))}
              style={{ resize: 'vertical' }}
            />
            <p style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 4 }}>
              {t('factuurteksten.hintAlgemeen')}
            </p>
          </div>

          {/* Sectie 2: Voetnoot per status */}
          <h3 style={{ fontSize: 13, fontWeight: 600, marginBottom: 12, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {t('factuurteksten.voetnootTitel')}
          </h3>
          <div style={{ display: 'grid', gap: 16, marginBottom: 32 }}>
            {[
              { key: 'invoice_footer_draft',        label: t('factuurteksten.labelFooterDraft') },
              { key: 'invoice_footer_sent',         label: t('factuurteksten.labelFooterSent') },
              { key: 'invoice_footer_reminded',     label: t('factuurteksten.labelFooterReminded') },
              { key: 'invoice_footer_final_notice', label: t('factuurteksten.labelFooterFinalNotice') },
              { key: 'invoice_footer_collections',  label: t('factuurteksten.labelFooterCollections') },
            ].map(({ key, label }) => (
              <div className="form-group" key={key} style={{ marginBottom: 0 }}>
                <label className="form-label">{label}</label>
                <textarea
                  className="form-input"
                  rows={2}
                  value={fields[key]}
                  onChange={e => setFields(p => ({ ...p, [key]: e.target.value }))}
                  style={{ resize: 'vertical' }}
                />
              </div>
            ))}
          </div>
          <p style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: -20, marginBottom: 32 }}>
            {t('factuurteksten.hintVoetnoot')}
          </p>

          {/* Sectie 3: Herinnerings- en aanmaningsteksten */}
          <h3 style={{ fontSize: 13, fontWeight: 600, marginBottom: 12, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {t('factuurteksten.herinneringTitel')}
          </h3>
          <div style={{ display: 'grid', gap: 16, marginBottom: 24 }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">{t('factuurteksten.labelReminderPdfText')}</label>
              <textarea
                className="form-input"
                rows={3}
                value={fields.reminder_pdf_text}
                onChange={e => setFields(p => ({ ...p, reminder_pdf_text: e.target.value }))}
                style={{ resize: 'vertical' }}
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">{t('factuurteksten.labelFinalNoticePdfText')}</label>
              <textarea
                className="form-input"
                rows={4}
                value={fields.final_notice_pdf_text}
                onChange={e => setFields(p => ({ ...p, final_notice_pdf_text: e.target.value }))}
                style={{ resize: 'vertical' }}
              />
            </div>
          </div>
          <p style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: -12, marginBottom: 24 }}>
            {t('factuurteksten.hintHerinnering')}
          </p>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? t('auth.loading') : t('factuurteksten.opslaan')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
